import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { axioKit, fullName } from "../../../../../../../services/utilities";
import {
  SETSOURCE,
  SETPHYSICIAN,
} from "../../../../../../../services/redux/slices/commerce/pos/services/taskGenerator";

/**
 * Fetches a list once and caches it in localStorage, using the same cache
 * keys as the cashier so both screens share the data.
 */
const useCachedList = (cacheKey, endpoint, key) => {
  const { token } = useSelector(({ auth }) => auth),
    [list, setList] = useState([]),
    query = JSON.stringify(key || {});

  useEffect(() => {
    if (!token || !cacheKey) return;

    const cached = localStorage.getItem(cacheKey);

    if (cached) {
      try {
        return setList(JSON.parse(cached) || []);
      } catch (error) {
        localStorage.removeItem(cacheKey);
      }
    }

    let isMounted = true;
    axioKit
      .universal(endpoint, token, JSON.parse(query))
      .then(({ payload = [] }) => {
        localStorage.setItem(cacheKey, JSON.stringify(payload));
        if (isMounted) setList(payload);
      })
      .catch((error) => console.error(`Error fetching ${endpoint}:`, error));

    return () => {
      isMounted = false;
    };
  }, [token, cacheKey, endpoint, query]);

  return list;
};

const Editable = ({ forwardedBy, physicianId, _id }) => {
  const { activePlatform } = useSelector(({ auth }) => auth),
    { branchId } = activePlatform || {},
    // referring sources are the providers where this branch is the vendor
    insources = useCachedList(
      branchId && `source_${branchId}`,
      "assets/providers/insource",
      { vendors: branchId, status: "approved" },
    ),
    allPhysicians = useCachedList("physicians", "assets/persons/physicians/browse"),
    [sourceId, setSourceId] = useState(forwardedBy?._id || "-"),
    [physician, setPhysician] = useState(physicianId?._id || "-"),
    dispatch = useDispatch();

  // clear selections left over from editing another card
  useEffect(() => {
    dispatch(SETSOURCE({ source: undefined, _id }));
    dispatch(SETPHYSICIAN({ physician: undefined, _id }));
  }, [_id, dispatch]);

  const isWalkin = !sourceId || sourceId === "-";
  const byName = (a, b) =>
    fullName(a.user.fullName).localeCompare(fullName(b.user.fullName));
  // one entry per physician (a user can have a record per branch)
  const withUser = (list = []) => {
    const seen = new Set();
    return list.filter(({ user }) => {
      if (!user?._id || seen.has(user._id)) return false;
      seen.add(user._id);
      return true;
    });
  };

  // physicians of the selected source: tagged to its branch or affiliated
  // filter by branch before de-duplicating, so no branch record is skipped
  const sourcePhysicians = isWalkin
    ? []
    : withUser([
        ...(insources.find(({ clients }) => clients?._id === sourceId)?.clients
          ?.affiliated || []),
        ...allPhysicians.filter(
          ({ branch }) => String(branch?._id || branch || "") === sourceId,
        ),
      ]).sort(byName);

  // walk in, or a source with no physicians yet: list everyone
  const others =
    isWalkin || !sourcePhysicians.length
      ? withUser(allPhysicians).sort(byName)
      : [];
  const listedIds = new Set(
    [...sourcePhysicians, ...others].map(({ user }) => user._id),
  );

  const renderOptions = (list) =>
    list.map(({ user }) => (
      <option key={user._id} value={user._id}>
        {fullName(user.fullName)}
      </option>
    ));

  const handleSource = ({ target }) => {
    setSourceId(target.value);
    setPhysician("-");
    dispatch(SETSOURCE({ source: target.value, _id }));
    dispatch(SETPHYSICIAN({ physician: "-", _id }));
  };

  const handlePhysician = ({ target }) => {
    setPhysician(target.value);
    dispatch(SETPHYSICIAN({ physician: target.value, _id }));
  };

  return (
    <div>
      <div className="sales-card-info">
        <small>Source</small>
        <select
          name="source"
          value={sourceId}
          onChange={handleSource}
          className="w-100"
        >
          <option value="-">Walk in</option>
          {insources
            .filter(({ clients }) => clients?._id)
            .map(({ _id: providerId, clients }) => (
              <option key={providerId} value={clients._id}>
                {String(clients.displayname || clients.name || "").toUpperCase()}
              </option>
            ))}
        </select>
      </div>
      <div className="sales-card-info">
        <small>Referral</small>
        <select
          name="physician"
          value={physician}
          onChange={handlePhysician}
          className="w-100"
        >
          <option value="-">No referral</option>
          {/* keep the current referral visible even if it is not listed */}
          {physicianId?._id &&
            physician === physicianId._id &&
            !listedIds.has(physicianId._id) && (
              <option value={physicianId._id}>
                {fullName(physicianId.fullName)}
              </option>
            )}
          {sourcePhysicians.length > 0 && renderOptions(sourcePhysicians)}
          {others.length > 0 &&
            (isWalkin ? (
              renderOptions(others)
            ) : (
              <optgroup label="No physicians tagged to this source, showing all">
                {renderOptions(others)}
              </optgroup>
            ))}
        </select>
      </div>
    </div>
  );
};

export default Editable;
